import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertLabTestComponentForm from 'components/settings/lab-test-component/UpsertLabTestComponentForm';
import React, { useEffect, useState } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Loader, Modal } from 'semantic-ui-react';
import {
  convertLegacyTestPanelTestsToOrderItemTest,
  createStandardComponents,
  deleteLabTestComponent,
  getAllLabTestComponents,
  getLabTestComponent
} from 'services/labTestComponentService';
import { LabTestComponentDto, UpsertLabTestComponentDto } from 'ts-types/api.types';

const cancelTokenSource = axios.CancelToken.source();

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
}

const LabTestComponentView = (props: Props) => {

  const {getMessage} = props;

  const [labTestComponents, setLabTestComponents] = useState<LabTestComponentDto[]>([]);
  const [labTestComponent, setLabTestComponent] = useState<UpsertLabTestComponentDto>();
  const [labTestComponentId, setLabTestComponentId] = useState<number>();
  const [addEditModalOpen, setAddEditModalOpen] = useState<boolean>(false);
  const [labTestComponentsLoaded, setLabTestComponentsLoaded] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    fetchLabTestComponents();
  }, []);

  const doCreateStandardComponents = async () => {
    createStandardComponents(cancelTokenSource);
  }

  const doConvertLegacyTestPanelTestsToOrderItemTest = async () => {
    convertLegacyTestPanelTestsToOrderItemTest(cancelTokenSource);
  }

  const fetchLabTestComponents = async () => {

    setLabTestComponentsLoaded(false);

    const onFinally = () => {
      setLabTestComponentsLoaded(true);
    }

    try {
      const labTestComponents = await getAllLabTestComponents(cancelTokenSource);
      setLabTestComponents(labTestComponents);
    } finally {
      onFinally();
    }
  }

  const openAddEditModal = async (id: number | undefined) => {
    if (id) {
      const labTestComponent = await getLabTestComponent(id, cancelTokenSource);
      setLabTestComponent(labTestComponent);
      setLabTestComponentId(id);
    }

    setAddEditModalOpen(true);
  }

  const onSaveUpsertForm = async () => {
    const labTestComponents = await getAllLabTestComponents(cancelTokenSource);
    setLabTestComponents(labTestComponents);
    setAddEditModalOpen(false);
  }

  const hideModal = () => {
    setLabTestComponentId(undefined);
    setLabTestComponent(undefined);
    setAddEditModalOpen(false);
  }

  const labTestComponentDelete = async (id: number) => {
    const filteredLabTestComponents = labTestComponents.filter(labTestComponent => labTestComponent.id != id);
    try {
      await deleteLabTestComponent(id, cancelTokenSource)
      setLabTestComponents(filteredLabTestComponents)
    } catch (e) {
      setErrorMessage(getMessage(`labTestComponent.${e.response.data.violations[0].errorCode}`));
    }
  }

  const renderLoader = (): React.ReactNode => {

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("labTestComponent.loading")}</Loader>
        </Dimmer>
    );
  };

  const actionCellRenderer = ({rowData}: any) => {

    return (
        <>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => openAddEditModal(rowData.id)}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              popupMessage={getMessage("labTestComponent.confirmDelete")}
              onConfirm={() => labTestComponentDelete(rowData.id)}/>
        </>
    )
  }

  const labTestComponentRowGetter = ({index}: any) => {

    if (labTestComponents) {
      Object.assign(labTestComponents[index], {index: index + 1});
      return labTestComponents[index];
    }
  }

  const labTestComponentRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role={"row"}
            style={style}
            onDoubleClick={() => openAddEditModal(labTestComponents && labTestComponents[index].id)}
        >
          {columns}
        </div>
    )
  }

  const renderLabTestComponents = (): JSX.Element => {

    return (
        <VirtualizedTable
            rowCount={labTestComponents.length}
            rowGetter={labTestComponentRowGetter}
            rowRenderer={labTestComponentRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "id"
              },
              {
                width: 300,
                label: (getMessage("labTestComponent.code")),
                dataKey: "code"
              },
              {
                width: 400,
                flexGrow: 1,
                label: (getMessage("labTestComponent.name")),
                dataKey: "name"
              },
              {
                width: 400,
                flexGrow: 1,
                label: (getMessage("labTestComponent.resultType")),
                dataKey: "resultType"
              },
              {
                width: 300,
                label: (getMessage("labTestComponent.scale")),
                dataKey: "scale"
              },
              {
                width: 300,
                label: (getMessage("labTestComponent.exampleUnits")),
                dataKey: "exampleUnits"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (actionCellRenderer)
              }
            ]}
        />
    )
  }

  const renderLabTestComponentModal = (): React.ReactNode => {
    return (
        <Modal
            open={addEditModalOpen}
            onClose={hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.labTestComponent")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertLabTestComponentForm
                labTestComponentId={labTestComponentId}
                labTestComponent={labTestComponent}
                onSave={onSaveUpsertForm}
                onCancel={hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

  const userRoles = props.currentUser ? props.currentUser.roles : [];
  const isSystemAdmin = userRoles.includes("ROLE_SYSTEM_ADMIN");

  return (
      <PraxStyledSegment>
        {errorMessage
            && (
                <StyledErrorMessage>
                  {errorMessage}
                </StyledErrorMessage>
            )
        }
        <div>
          <PxGrid>
            <Grid.Row>
              <Grid.Column width={3} verticalAlign="middle">
                {
                    isSystemAdmin &&
                    <Button compact onClick={() => doCreateStandardComponents()}>Create standard components</Button>
                }
              </Grid.Column>
              <Grid.Column width={3} verticalAlign="middle">
                {
                    isSystemAdmin &&
                    <Button compact onClick={() => doConvertLegacyTestPanelTestsToOrderItemTest()}>
                      Convert legacy interventions
                    </Button>
                }
              </Grid.Column>
              <Grid.Column width={4} verticalAlign={"top"}>
              </Grid.Column>
              <Grid.Column width={6}>
                <AddButton buttonMessage={getMessage("button.add")} onClick={() => setAddEditModalOpen(true)}/>
              </Grid.Column>
            </Grid.Row>
          </PxGrid>
        </div>
        <StyledViewTableRow>
          {!labTestComponentsLoaded ? renderLoader() : renderLabTestComponents()}
        </StyledViewTableRow>

        {renderLabTestComponentModal()}
      </PraxStyledSegment>
  )
}

export default withAuthContext(withMessages(LabTestComponentView));
